/**
 * Reference-data GET cache (frontend).
 *
 * A tiny module-level TTL cache for the near-static reference-data feeds that the
 * contract forms fan out on every mount (frequencies, countries, VAT rates, etc.).
 * Collapses "open form -> cancel -> reopen" from N calls to 0 within the TTL, and
 * dedupes concurrent identical requests (React StrictMode double-mount / parallel
 * fan-out) by caching the in-flight Promise, not just the resolved value.
 *
 * SECURITY (multi-tenant): every entry is keyed on the endpoint + the CURRENT
 * selectedTenantId read from localStorage AT CALL TIME. A tenant change — including
 * the two no-reload paths (TenantContext state sync + apiClient X-Tenant-Resolved
 * self-heal) — therefore produces a cache MISS for the new tenant, so cross-tenant
 * data is never served. Do NOT key on endpoint alone, and do NOT capture the tenant
 * id once at module load. As defence-in-depth, a `storage` listener clears the whole
 * cache whenever selectedTenantId changes.
 *
 * Use ONLY for feeds that are effectively static per tenant. Do NOT cache mutable
 * feeds (investors list, interest rates, commission rates) — a user adding one
 * mid-session must see it immediately.
 */

import { apiGet } from './apiClient';

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

// key -> { value, expires }   (resolved responses)
const valueCache = new Map();
// key -> Promise            (in-flight requests, deduped)
const inflight = new Map();

const currentTenantId = () => {
  try { return localStorage.getItem('selectedTenantId') || 'none'; } catch (e) { return 'none'; }
};

const cacheKey = (url) => `${currentTenantId()}::${url}`;

/**
 * Cached GET. Returns the same shape apiGet returns (response body).
 * @param {string} url
 * @param {{ ttl?: number }} [opts]
 */
export const cachedApiGet = async (url, opts = {}) => {
  const ttl = opts.ttl ?? DEFAULT_TTL_MS;
  const key = cacheKey(url);

  const hit = valueCache.get(key);
  if (hit && hit.expires > Date.now()) {
    return hit.value;
  }

  // Dedupe concurrent identical requests: return the in-flight Promise if one exists.
  const pending = inflight.get(key);
  if (pending) return pending;

  const p = apiGet(url)
    .then((value) => {
      valueCache.set(key, { value, expires: Date.now() + ttl });
      inflight.delete(key);
      return value;
    })
    .catch((err) => {
      // Never cache failures — let the next call retry.
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, p);
  return p;
};

/** Clear the entire cache (both resolved values and in-flight map). */
export const clearRefDataCache = () => {
  valueCache.clear();
  inflight.clear();
};

// Defence-in-depth: if selectedTenantId changes in another tab (or via any code path
// that writes localStorage), drop everything so no stale cross-tenant data lingers.
// (Same-tab writes don't fire 'storage', but the per-call tenant-scoped key already
// covers those — this catches the cross-tab case.)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'selectedTenantId') clearRefDataCache();
  });
}

export default cachedApiGet;
